/**
 * Client-side prediction + reconciliation for the LOCAL avatar in a port instance (Onda 6) — the
 * on-foot twin of prediction.ts. We step the exact same `stepWalkerBody`/`collidePort` the server runs,
 * so the avatar responds instantly; each authoritative port snapshot snaps us to truth and replays the
 * inputs the server hadn't processed yet. Same code ⇒ no drift.
 */
import { INPUT_HZ, PORT, PORT_LAYOUT, WALK, type PortObstacle } from '@piratas/protocol';
import { collidePort, makeWalkerBody, stepWalkerBody, type WalkerBody, type WalkerInput } from '@piratas/sim';
import type { PortSelf } from '@piratas/protocol';

const INPUT_DT = 1 / INPUT_HZ;

export class PortPrediction {
  body: WalkerBody = makeWalkerBody(PORT_LAYOUT.spawn.x, PORT_LAYOUT.spawn.y, PORT_LAYOUT.spawn.heading);
  hasBaseline = false;
  /** Onda 6.5: the active layout's collision set + plaza size — MUST match the server PortRoom's layout
   *  (village or capital) so predicted collision == authority. Defaults to the village plaza. */
  private obstacles: readonly PortObstacle[] = PORT_LAYOUT.obstacles;
  private size: number = PORT.SIZE;
  private pending: { seq: number; input: WalkerInput }[] = [];

  /** Point prediction at the layout of the port just entered (capital vs village). */
  setLayout(obstacles: readonly PortObstacle[], size: number): void {
    this.obstacles = obstacles; this.size = size;
  }

  /** Apply one fresh local input step (called at INPUT_HZ) and remember it. */
  applyInput(seq: number, input: WalkerInput): void {
    if (!this.hasBaseline) return;
    stepWalkerBody(this.body, input, INPUT_DT);
    collidePort(this.body, this.obstacles, WALK.RADIUS, this.size);
    this.pending.push({ seq, input: { ...input } });
    if (this.pending.length > 256) this.pending.shift();
  }

  /** Reconcile against the authoritative self-avatar from the latest port snapshot. */
  reconcile(auth: PortSelf, ackSeq: number): void {
    this.body.x = auth.x;
    this.body.y = auth.y;
    this.body.heading = auth.heading;
    this.body.vx = auth.vx;
    this.body.vy = auth.vy;
    this.pending = this.pending.filter((p) => p.seq > ackSeq);
    for (const p of this.pending) {
      stepWalkerBody(this.body, p.input, INPUT_DT);
      collidePort(this.body, this.obstacles, WALK.RADIUS, this.size);
    }
    this.hasBaseline = true;
  }

  /** Initialize the predicted avatar directly from an authoritative state. */
  initFrom(auth: PortSelf): void {
    this.body.x = auth.x; this.body.y = auth.y; this.body.heading = auth.heading;
    this.body.vx = auth.vx; this.body.vy = auth.vy;
    this.pending.length = 0;
    this.hasBaseline = true;
  }
}
